import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MessageCircle, Send } from "lucide-react";
import { getBlogBySlug, BLOG_BODIES } from "@/data/blogPosts";

/**
 * Data-driven blog post renderer.
 * Reads slug from URL, looks up metadata + body, renders editorial layout.
 */

const DARLING_SLUG = "darling-thats-not-jewelry";

const COMMENT_STORAGE_KEY = "gg_blog_comments_v1";

const formatCommentDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

// Seeded comments (in addition to the pinned Editor's Pick above)
const SEEDED_COMMENTS = [
  {
    id: "seed-riva",
    name: "Riva",
    date: "2026-06-20",
    text: "Pearls all the way ✨ The Blair Waldorf reference hit different.",
  },
  {
    id: "seed-ananya",
    name: "Ananya",
    date: "2026-06-21",
    text: "Hoops girl forever — gold hoops on Tuesdays should be law.",
  },
];

const romanise = (n) => {
  const map = [
    ["X", 10],
    ["IX", 9],
    ["V", 5],
    ["IV", 4],
    ["I", 1],
  ];
  let r = "";
  let x = n;
  map.forEach(([sym, val]) => {
    while (x >= val) {
      r += sym;
      x -= val;
    }
  });
  return r;
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const BlogPost = () => {
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const slug = slugParam || DARLING_SLUG;
  const post = getBlogBySlug(slug);

  // Local comments (persisted in localStorage per device)
  const [userComments, setUserComments] = useState([]);
  const [nameInput, setNameInput] = useState("");
  const [textInput, setTextInput] = useState("");

  // Reading progress bar — slim gold strip at very top of the viewport
  const [readProgress, setReadProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY || doc.scrollTop;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.min(100, Math.max(0, (scrolled / max) * 100));
      setReadProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [slug]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COMMENT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.[slug])) setUserComments(parsed[slug]);
      }
    } catch {
      /* ignore */
    }
  }, [slug]);

  const submitComment = () => {
    const name = nameInput.trim() || "Anonymous";
    const text = textInput.trim();
    if (!text) return;
    const c = {
      id: `u-${Date.now()}`,
      name,
      text,
      date: new Date().toISOString(),
    };
    const next = [c, ...userComments];
    setUserComments(next);
    setNameInput("");
    setTextInput("");
    try {
      const raw = localStorage.getItem(COMMENT_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[slug] = next;
      localStorage.setItem(COMMENT_STORAGE_KEY, JSON.stringify(all));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!post) navigate("/blog", { replace: true });
  }, [post, navigate]);

  useEffect(() => {
    if (!post) return undefined;
    const prev = document.title;
    document.title = `${post.title} — GirlyGrove Journal`;
    return () => {
      document.title = prev;
    };
  }, [post]);

  if (!post) return null;

  const blocks = BLOG_BODIES[slug] || [];
  const firstParaIdx = blocks.findIndex((b) => b.type === "paragraph");
  let chapterIdx = 0;

  // Reading time — use pre-defined readTime from post metadata (e.g. "6 min read")
  const readTimeText = (post.readTime || "5 min read").toUpperCase();
  const chipOpacity = Math.max(0, 1 - readProgress / 45); // fades out by ~45% scroll
  const isFinished = readProgress >= 96;

  return (
    <div data-testid="blog-post-page" className="bg-cream text-plum">
      {/* Reading progress bar — slim gold strip at the very top */}
      <div
        className="fixed top-0 left-0 right-0 z-[100] h-[3px] pointer-events-none"
        aria-hidden
        data-testid="blog-reading-progress"
      >
        <div
          className="h-full transition-[width] duration-150 ease-out"
          style={{
            width: `${readProgress}%`,
            background:
              "linear-gradient(90deg, #C5985A 0%, #F4D58D 45%, #E0BC76 75%, #D4537E 100%)",
            boxShadow: "0 0 8px rgba(197,152,90,0.55)",
          }}
        />
      </div>

      {/* Reading time chip — fades out as user scrolls, flips to ✦ FINISHED at the end */}
      <div
        className="fixed top-[86px] right-4 sm:right-6 z-[101] pointer-events-none"
        aria-hidden
        data-testid="blog-read-chip"
      >
        {isFinished ? (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-semibold text-plum"
            style={{
              background: "linear-gradient(135deg,#FBEED6,#F4D58D)",
              boxShadow:
                "0 4px 14px -4px rgba(197,152,90,0.55), inset 0 0 0 1px rgba(197,152,90,0.5)",
              opacity: 1,
              transition: "opacity 400ms ease-out",
            }}
          >
            ✦ Finished
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-medium text-plum"
            style={{
              background: "rgba(253,246,240,0.85)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              boxShadow:
                "0 4px 14px -6px rgba(74,44,53,0.18), inset 0 0 0 1px rgba(197,152,90,0.35)",
              opacity: chipOpacity,
              transition: "opacity 200ms ease-out",
            }}
          >
            <span className="text-plum/70">☕</span>
            {readTimeText}
          </span>
        )}
      </div>
      <style>{`
        .gg-blog-dropcap::first-letter {
          font-family: 'Cormorant Garamond','Playfair Display', serif;
          font-style: italic;
          float: left;
          font-size: 4.6em;
          line-height: 0.86;
          margin: 0.08em 0.12em 0 0;
          background: linear-gradient(135deg, #C5985A 0%, #E0BC76 50%, #B33866 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        @keyframes ggBpFade {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .gg-bp-fade { animation: ggBpFade 700ms cubic-bezier(0.22,1,0.36,1) both; }
        @keyframes ggMorpankh {
          0%,100% { transform: rotate(-6deg) translateY(0); }
          50%     { transform: rotate(6deg)  translateY(-2px); }
        }
        .gg-morpankh { transform-origin: bottom center; animation: ggMorpankh 4.5s ease-in-out infinite; }
      `}</style>

      {/* HERO --------------------------------------------------------- */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #FDF6F0 0%, #FBEAF0 60%, #FDF6F0 100%)",
          }}
        />
        <div className="relative max-w-[1280px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-12 lg:pb-20">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-plum/60 hover:text-blush mb-6 transition-colors"
            data-testid="blog-back-index"
          >
            <ArrowLeft size={13} /> GirlyGrove Journal
          </Link>

          <p
            className="text-[11px] lg:text-[12px] tracking-[0.32em] uppercase font-semibold mb-4"
            style={{ color: "#C5985A" }}
          >
            An Editorial · {post.readTime}
          </p>

          <h1
            className="font-serif text-plum leading-[1.02] tracking-tight gg-bp-fade"
            style={{ fontSize: "clamp(34px, 5.4vw, 72px)" }}
            data-testid="blog-title"
          >
            {post.title}
          </h1>

          <p
            className="mt-4 lg:mt-6 max-w-[680px] text-plum/75 font-serif italic gg-bp-fade"
            style={{ fontSize: "clamp(17px, 2vw, 24px)", animationDelay: "150ms" }}
          >
            {post.dek}
          </p>

          {/* Meta row */}
          <div
            className="mt-5 flex items-center gap-4 text-[11px] tracking-[0.18em] uppercase text-plum/55 gg-bp-fade"
            style={{ animationDelay: "220ms" }}
          >
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={12} strokeWidth={2} /> {formatDate(post.date)}
            </span>
            <span className="text-plum/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} strokeWidth={2} /> {post.readTime}
            </span>
          </div>

          {/* Banner */}
          <div
            className="relative mt-10 lg:mt-14 rounded-[28px] overflow-hidden shadow-[0_40px_90px_-30px_rgba(74,44,53,0.45)] gg-bp-fade"
            style={{ aspectRatio: "16/8", animationDelay: "320ms" }}
          >
            <img
              src={post.banner}
              alt={post.bannerAlt}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              draggable="false"
            />
          </div>
          <div className="mt-3 lg:mt-4 flex flex-col sm:flex-row items-center sm:items-baseline justify-between gap-1 sm:gap-3 px-1 text-plum/70">
            <p className="text-[10px] lg:text-[11px] tracking-[0.32em] uppercase font-semibold text-plum">
              ✦ A GirlyGrove Editorial
            </p>
            <p className="text-[10px] lg:text-[11px] tracking-[0.18em] uppercase">
              {post.volume}
            </p>
          </div>
        </div>
      </section>

      {/* BODY ---------------------------------------------------------- */}
      <article
        className="max-w-[760px] mx-auto px-5 lg:px-10 pt-8 lg:pt-16 pb-6"
        data-testid="blog-body"
      >
        {blocks.map((block, i) => {
          if (block.type === "heading") {
            chapterIdx += 1;
            return (
              <div key={i} className="mt-14 lg:mt-20 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="text-[10px] lg:text-[11px] tracking-[0.42em] uppercase font-semibold flex-shrink-0"
                    style={{ color: "#C5985A" }}
                  >
                    Chapter {romanise(chapterIdx)}
                  </span>
                  <span
                    className="h-px flex-1"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(197,152,90,0.55), rgba(197,152,90,0.05))",
                    }}
                  />
                </div>
                <h2
                  className="font-serif text-plum leading-[1.04] tracking-tight"
                  style={{ fontSize: "clamp(28px, 3.8vw, 46px)" }}
                >
                  <span className="italic">{block.text}</span>
                </h2>
              </div>
            );
          }
          if (block.type === "pullquote") {
            return (
              <blockquote
                key={i}
                className="my-12 lg:my-16 mx-auto max-w-[640px] text-center font-serif italic text-plum leading-snug relative px-6 lg:px-12 py-8 lg:py-10 rounded-[20px]"
                style={{
                  fontSize: "clamp(20px, 2.4vw, 30px)",
                  background:
                    "linear-gradient(135deg, #FDF6F0 0%, #F6EBD8 100%)",
                  border: "1px solid rgba(197,152,90,0.32)",
                  boxShadow: "0 18px 36px -22px rgba(74,44,53,0.28)",
                }}
              >
                <span
                  className="absolute left-4 top-1 select-none"
                  style={{
                    fontSize: 56,
                    color: "#C5985A",
                    opacity: 0.45,
                    fontFamily: "Cormorant Garamond, serif",
                  }}
                  aria-hidden
                >
                  “
                </span>
                {block.text}
                <span
                  className="absolute right-4 -bottom-2 select-none"
                  style={{
                    fontSize: 56,
                    color: "#C5985A",
                    opacity: 0.45,
                    fontFamily: "Cormorant Garamond, serif",
                  }}
                  aria-hidden
                >
                  ”
                </span>
              </blockquote>
            );
          }
          const isFirstPara = i === firstParaIdx;
          return (
            <p
              key={i}
              className={`text-plum/90 font-serif mt-5 ${isFirstPara ? "gg-blog-dropcap" : ""}`}
              style={{ fontSize: "clamp(17px, 1.25vw, 20px)", lineHeight: 1.78 }}
            >
              {block.text}
            </p>
          );
        })}

        {/* Closing flourish */}
        <div
          className="mt-16 lg:mt-20 flex items-center justify-center gap-4"
          aria-hidden
        >
          <span
            className="h-px w-16"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(197,152,90,0.55), transparent)",
            }}
          />
          <span style={{ color: "#C5985A", fontSize: 22 }}>✦</span>
          <span
            className="h-px w-16"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(197,152,90,0.55), transparent)",
            }}
          />
        </div>
      </article>

      {/* AUTHOR SIGNATURE + PINNED COMMENT ---------------------------------- */}
      <section className="max-w-[860px] mx-auto px-5 lg:px-10 mt-10 lg:mt-16">
        <style>{`
          @keyframes bpHeart {
            0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.9; }
            50%      { transform: scale(1.14) rotate(-4deg); opacity: 1; }
          }
          @keyframes bpMorpankhSway {
            0%, 100% { transform: rotate(-9deg) translateY(0); }
            50%      { transform: rotate(6deg) translateY(-3px); }
          }
          @keyframes bpEyeShimmer {
            0%, 100% { filter: brightness(1) saturate(1); }
            50%      { filter: brightness(1.15) saturate(1.25); }
          }
          @keyframes bpBarbBreathe {
            0%, 100% { transform: scaleX(1); opacity: 0.85; }
            50%      { transform: scaleX(1.04); opacity: 1; }
          }
          @keyframes bpFeatherDrift {
            0%   { transform: translate(-20px, 0) rotate(-8deg); opacity: 0; }
            15%  { opacity: 0.55; }
            85%  { opacity: 0.55; }
            100% { transform: translate(120px, -20px) rotate(22deg); opacity: 0; }
          }
          .bp-heart      { animation: bpHeart 1.9s ease-in-out infinite; display: inline-block; }
          .bp-morpankh   { animation: bpMorpankhSway 4.2s ease-in-out infinite; display: inline-block; transform-origin: 50% 95%; }
          .bp-mp-eye     { animation: bpEyeShimmer 3.2s ease-in-out infinite; transform-origin: 50% 50%; }
          .bp-mp-plumes  { animation: bpBarbBreathe 5s ease-in-out infinite; transform-origin: 50% 100%; }
          .bp-feather-a  { animation: bpFeatherDrift 5.5s linear 0.4s infinite; }
          .bp-feather-b  { animation: bpFeatherDrift 6.5s linear 1.6s infinite; }
        `}</style>

        <div
          className="relative flex flex-col sm:flex-row items-center text-center sm:text-left gap-4 sm:gap-5 rounded-[24px] p-5 lg:p-7 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #FDF6F0 0%, #F6EBD8 100%)",
            border: "1px solid rgba(197,152,90,0.3)",
          }}
          data-testid="blog-author"
        >
          {/* Background drifting feathers */}
          <span
            className="bp-feather-a absolute text-2xl pointer-events-none select-none"
            style={{ left: "12%", top: "22%", opacity: 0.5 }}
            aria-hidden
          >
            🪶
          </span>
          <span
            className="bp-feather-b absolute text-xl pointer-events-none select-none"
            style={{ left: "38%", top: "62%", opacity: 0.5 }}
            aria-hidden
          >
            🪶
          </span>

          {/* Aastha's avatar — real photo, conic gold/blush ring, brightened */}
          <div
            className="relative flex-shrink-0 w-20 h-20 rounded-full overflow-hidden p-[3px]"
            style={{
              background:
                "conic-gradient(from 140deg, #E0BC76, #F4D58D, #D4537E, #B33866, #C5985A, #E0BC76)",
              boxShadow: "0 10px 26px -12px rgba(74,44,53,0.45)",
            }}
            data-testid="blog-author-avatar"
          >
            {post.authorImage ? (
              <img
                src={post.authorImage}
                alt={post.author}
                className="w-full h-full rounded-full object-cover"
                style={{ filter: "brightness(1.18) contrast(1.08) saturate(1.15)" }}
                draggable="false"
                loading="lazy"
              />
            ) : (
              <div
                className="w-full h-full rounded-full flex items-center justify-center"
                style={{ background: "#4A2C35" }}
              >
                <span className="text-cream font-serif italic text-2xl">A</span>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1 relative z-10">
            <p className="text-[10px] tracking-[0.28em] uppercase text-plum/65 mb-1">
              Penned by
            </p>
            <p
              className="font-serif text-plum leading-none"
              style={{ fontSize: "clamp(22px, 2.4vw, 30px)" }}
            >
              {post.author}
            </p>
          </div>

          {/* Heart + Morpankh — leaf-shape peacock feather with iridescent eye */}
          <div className="relative flex items-center gap-2 z-10 flex-shrink-0" aria-hidden>
            <span className="bp-heart text-2xl text-blush">♥</span>
            <span className="bp-morpankh block" style={{ width: 82, height: 140 }}>
              <svg
                viewBox="0 0 82 140"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ overflow: "visible" }}
              >
                <defs>
                  {/* Iridescent barb — deep teal → emerald → gold tip */}
                  <linearGradient id="mp2-barb-l" x1="1" y1="0" x2="0" y2="0.35">
                    <stop offset="0%"  stopColor="#0E3A34" />
                    <stop offset="35%" stopColor="#1F6B5F" />
                    <stop offset="65%" stopColor="#6FA88F" />
                    <stop offset="100%" stopColor="#F1D48A" stopOpacity="0.85" />
                  </linearGradient>
                  <linearGradient id="mp2-barb-r" x1="0" y1="0" x2="1" y2="0.35">
                    <stop offset="0%"  stopColor="#0E3A34" />
                    <stop offset="35%" stopColor="#1F6B5F" />
                    <stop offset="65%" stopColor="#6FA88F" />
                    <stop offset="100%" stopColor="#F1D48A" stopOpacity="0.85" />
                  </linearGradient>

                  {/* Rich iridescent peacock eye */}
                  <radialGradient id="mp2-eye-halo" cx="50%" cy="50%" r="60%">
                    <stop offset="0%"  stopColor="#3F8672" stopOpacity="0.9" />
                    <stop offset="70%" stopColor="#1B4D45" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#0A2521" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient id="mp2-eye-outer" cx="50%" cy="45%" r="58%">
                    <stop offset="0%"  stopColor="#2A6E62" />
                    <stop offset="55%" stopColor="#123934" />
                    <stop offset="100%" stopColor="#061815" />
                  </radialGradient>
                  <radialGradient id="mp2-eye-ring" cx="50%" cy="45%" r="55%">
                    <stop offset="0%"  stopColor="#A6E4CE" />
                    <stop offset="50%" stopColor="#3F8E7F" />
                    <stop offset="100%" stopColor="#0F3A34" />
                  </radialGradient>
                  <radialGradient id="mp2-eye-core" cx="42%" cy="38%" r="60%">
                    <stop offset="0%"  stopColor="#FFF8DC" />
                    <stop offset="50%" stopColor="#F0C568" />
                    <stop offset="100%" stopColor="#7A5324" />
                  </radialGradient>
                  <radialGradient id="mp2-eye-pupil" cx="45%" cy="45%" r="55%">
                    <stop offset="0%"  stopColor="#1B4E44" />
                    <stop offset="80%" stopColor="#062119" />
                    <stop offset="100%" stopColor="#000" />
                  </radialGradient>

                  {/* Golden filament rachis (stem) */}
                  <linearGradient id="mp2-stem" x1="0.5" y1="1" x2="0.5" y2="0">
                    <stop offset="0%"  stopColor="#5B3D22" />
                    <stop offset="60%" stopColor="#B58748" />
                    <stop offset="100%" stopColor="#F1D48A" />
                  </linearGradient>
                </defs>

                {/* Halo glow behind eye */}
                <ellipse cx="41" cy="34" rx="34" ry="32" fill="url(#mp2-eye-halo)" />

                {/* --- LEAF-SHAPED PLUMES (upper canopy of barbs) --- */}
                <g className="bp-mp-plumes">
                  {Array.from({ length: 26 }).map((_, i) => {
                    // Distribute barbs across a fan from ~-95deg (left) to +95deg (right)
                    const angle = -95 + (i / 25) * 190;
                    const rad = (angle * Math.PI) / 180;
                    // Barbs go OUTWARD from the eye centre (41, 34)
                    const baseR = 18;
                    const tipR  = 40 + Math.abs(Math.cos(rad)) * 4;
                    const x1 = 41 + Math.sin(rad) * baseR;
                    const y1 = 34 - Math.cos(rad) * baseR * 0.9;
                    const x2 = 41 + Math.sin(rad) * tipR;
                    // Barbs droop slightly downward on the sides
                    const droop = 4 + Math.abs(Math.sin(rad)) * 6;
                    const y2 = 34 - Math.cos(rad) * tipR * 0.85 + droop;
                    const grad = angle < 0 ? "mp2-barb-l" : "mp2-barb-r";
                    return (
                      <path
                        key={`up${i}`}
                        d={`M${x1} ${y1} Q ${(x1 + x2) / 2} ${(y1 + y2) / 2 - 1.5} ${x2} ${y2}`}
                        stroke={`url(#${grad})`}
                        strokeWidth={0.9}
                        strokeLinecap="round"
                        fill="none"
                        opacity={0.85 - Math.abs(angle) / 220}
                      />
                    );
                  })}
                </g>

                {/* --- LOWER FLOWING PLUMES (like real feather barbs cascading down) --- */}
                <g>
                  {Array.from({ length: 40 }).map((_, i) => {
                    const t = i / 39;
                    const yStart = 56 + t * 60;
                    // Alternate sides
                    const side = i % 2 === 0 ? -1 : 1;
                    const inset = i % 2 === 0 ? 0 : 1;
                    // Barbs get shorter as they cascade downward
                    const len = 24 * (1 - t * 0.7) + 3;
                    // Slight droop
                    const droop = 3 + t * 5;
                    const xStart = 41 + side * inset;
                    const xEnd = 41 + side * len;
                    const yEnd = yStart + droop;
                    const grad = side < 0 ? "mp2-barb-l" : "mp2-barb-r";
                    return (
                      <path
                        key={`dn${i}`}
                        d={`M${xStart} ${yStart} Q ${(xStart + xEnd) / 2} ${yStart + 1} ${xEnd} ${yEnd}`}
                        stroke={`url(#${grad})`}
                        strokeWidth={0.75 - t * 0.25}
                        strokeLinecap="round"
                        fill="none"
                        opacity={0.75 - t * 0.35}
                      />
                    );
                  })}
                </g>

                {/* Curved golden stem — from tip through eye */}
                <path
                  d="M41 132 Q 42 100 41 70 Q 40 50 41 34"
                  stroke="url(#mp2-stem)"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />

                {/* --- PEACOCK EYE (chandrak) — layered iridescence --- */}
                <g className="bp-mp-eye">
                  {/* Outer dark almond */}
                  <ellipse cx="41" cy="34" rx="14" ry="17" fill="url(#mp2-eye-outer)" />
                  {/* Teal iridescent ring */}
                  <ellipse cx="41" cy="34" rx="11" ry="14" fill="url(#mp2-eye-ring)" />
                  {/* Bright gold-cream halo */}
                  <ellipse cx="41" cy="34" rx="7.5" ry="10" fill="url(#mp2-eye-core)" />
                  {/* Dark central pupil */}
                  <ellipse cx="41" cy="34" rx="4" ry="5.5" fill="url(#mp2-eye-pupil)" />
                  {/* Bright sparkle highlight */}
                  <ellipse cx="39.2" cy="30.8" rx="1.4" ry="2" fill="#FFFDF3" opacity="0.95" />
                  {/* Tiny secondary sparkle */}
                  <ellipse cx="43" cy="37" rx="0.7" ry="1" fill="#FFFDF3" opacity="0.6" />
                </g>

                {/* Thin gold radial fringe around eye — spikes fanning outward */}
                {Array.from({ length: 20 }).map((_, i) => {
                  const angle = -95 + (i / 19) * 190;
                  const rad = (angle * Math.PI) / 180;
                  const x1 = 41 + Math.sin(rad) * 15;
                  const y1 = 34 - Math.cos(rad) * 18;
                  const x2 = 41 + Math.sin(rad) * 19;
                  const y2 = 34 - Math.cos(rad) * 22;
                  return (
                    <line
                      key={`fr${i}`}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="#E6C078"
                      strokeWidth="0.55"
                      strokeLinecap="round"
                      opacity="0.75"
                    />
                  );
                })}
              </svg>
            </span>
          </div>
        </div>

        {/* ── Conversations (with pinned Editor's Pick + input form) ─── */}
        <div className="mt-12 lg:mt-16" data-testid="blog-comments">
          <div className="flex items-center justify-between mb-6">
            <h3
              className="font-serif text-plum leading-tight inline-flex items-center gap-3"
              style={{ fontSize: "clamp(24px, 3vw, 36px)" }}
            >
              <MessageCircle size={22} strokeWidth={1.8} className="text-gold" />
              Conversations
              <span className="text-[11px] tracking-[0.28em] uppercase text-plum/55 font-semibold align-middle">
                {3 + userComments.length} thoughts
              </span>
            </h3>
          </div>

          {/* ── Join the conversation form ─────────────────────────── */}
          <div
            className="rounded-[20px] p-5 lg:p-6 mb-6"
            style={{
              background:
                "linear-gradient(135deg, #FBEAF0 0%, #F9F2EC 60%, #FBEED6 100%)",
              border: "1px solid rgba(197,152,90,0.28)",
            }}
            data-testid="blog-comment-form"
          >
            <p className="font-serif text-plum text-[20px] lg:text-[22px] leading-tight">
              Join the conversation
            </p>
            <p className="text-plum/70 text-sm mt-1 mb-4">
              Are you a pearl or a hoop? Tell us in a sentence.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Your name"
                className="px-4 py-3 rounded-full text-sm text-plum bg-cream border border-gold/25 focus:outline-none focus:border-gold placeholder:text-plum/40"
                data-testid="blog-comment-name"
              />
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                placeholder="Drop your thoughts…"
                className="px-4 py-3 rounded-full text-sm text-plum bg-cream border border-gold/25 focus:outline-none focus:border-gold placeholder:text-plum/40"
                data-testid="blog-comment-text"
              />
            </div>
            <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
              <p className="text-[10px] tracking-[0.28em] uppercase text-plum/55 font-semibold">
                Comments stay on this device ✧ Be kind
              </p>
              <button
                onClick={submitComment}
                disabled={!textInput.trim()}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[11px] tracking-[0.24em] uppercase font-semibold text-plum shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background:
                    "linear-gradient(135deg, #F4D58D 0%, #E0BC76 55%, #C5985A 100%)",
                }}
                data-testid="blog-comment-submit"
              >
                <Send size={12} strokeWidth={2.4} />
                Post
              </button>
            </div>
          </div>

          {/* Harshad Pale — Editor's Pick pinned comment */}
          <div
            className="rounded-[20px] p-5 lg:p-6 mb-5 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #FBEAD0 0%, #F4D58D 55%, #E5C68E 100%)",
              border: "1px solid rgba(197,152,90,0.55)",
              boxShadow: "0 14px 32px -18px rgba(74,44,53,0.35)",
            }}
            data-testid="blog-comment-pinned"
          >
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #4A2C35 0%, #2B1A20 100%)",
                  }}
                >
                  <span className="text-cream font-serif italic text-base">
                    H
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-plum text-[16px] leading-none">
                      Harshad Pale
                    </p>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8.5px] tracking-[0.22em] uppercase font-bold text-plum"
                      style={{
                        background:
                          "linear-gradient(135deg, #FFFDF8 0%, #F4D58D 100%)",
                        border: "1px solid rgba(74,44,53,0.28)",
                      }}
                    >
                      ✦ Editor&apos;s Pick
                    </span>
                  </div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-plum/70 mt-1">
                    Pinned · Founder&apos;s note
                  </p>
                </div>
              </div>
              <span className="text-[10.5px] tracking-[0.18em] uppercase text-plum/60 font-semibold whitespace-nowrap">
                Jun 22, 2026
              </span>
            </div>
            <p className="text-plum/85 leading-relaxed text-[14.5px] lg:text-[15px]">
              Reading this felt like overhearing a perfect conversation. The
              Blair-vs-Serena framing is razor-sharp — equal parts witty and
              elegant. Honestly, GirlyGrove&apos;s voice is becoming a style of
              its own. Bookmarked. ✦
            </p>
          </div>

          {/* User-submitted comments (newest first) */}
          {userComments.map((c) => (
            <CommentRow key={c.id} name={c.name} text={c.text} date={c.date} />
          ))}

          {/* Seeded regular comments */}
          {SEEDED_COMMENTS.map((c) => (
            <CommentRow key={c.id} name={c.name} text={c.text} date={c.date} />
          ))}
        </div>
      </section>

      <div className="pb-24" />
    </div>
  );
};

// Regular comment row — used for both seeded and user comments
const CommentRow = ({ name, text, date }) => (
  <div
    className="rounded-[18px] p-5 mb-4"
    style={{
      background: "#FBEAF0",
      border: "1px solid rgba(197,152,90,0.22)",
    }}
    data-testid={`blog-comment-${name.replace(/[^a-z]/gi, "").toLowerCase()}`}
  >
    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "#E8A0B4" }}
        >
          <span className="text-cream font-serif italic text-sm">
            {name[0]}
          </span>
        </div>
        <p className="font-serif text-plum text-[15px] leading-none">
          {name}
        </p>
      </div>
      <span className="text-[10.5px] tracking-[0.18em] uppercase text-plum/55 font-semibold whitespace-nowrap">
        {formatCommentDate(date)}
      </span>
    </div>
    <p className="text-plum/80 leading-relaxed text-[14px]">{text}</p>
  </div>
);

export default BlogPost;
